import { Stack } from '@chakra-ui/react';
import { useAppContext, useEvergreen } from '@frx/frx-react-context';

import { FC, useEffect, useState } from 'react';
import {
  GetEvergreenResponse,
  Participant,
  Team as TypeOfTeam,
} from '@frx/frx-services';
import { TeamInvitation, TeamMembers, YourTeam } from './columns';
import { CreateTeam } from './columns/create-team/CreateTeam';
import { JoinTeam } from './columns/join-team/JoinTeam';
import { Wrapper } from '../../components';
import { CaptainTools } from './columns/captain-tools/CaptainTools';
import {
  getContentFragment,
  getFullImagePath,
  FragmentIdentifier,
  shuffleArray,
  isATeamCaptain,
  TeamMemberProps,
} from '../../utils';
import { useEventId, useTeams } from '../../hooks';

type TeamProps = {
  headline: string;
  description: string;
  imgPath: string;
};

/**
 * @description
 *  Helper method to locate a CMS fragment in an array
 * @param evergreen
 * @param id fragment identifier
 * @returns
 */
const teamProps = (
  evergreen: GetEvergreenResponse | undefined,
  id: string
): TeamProps => {
  const createTeamFragment = getContentFragment(
    evergreen?.data.evergreenList?.items || [],
    id
  );

  const headline = createTeamFragment?.headline;
  const description = createTeamFragment?.description?.html;
  const imgPath = getFullImagePath(createTeamFragment?.image[0]?._path);

  return {
    headline,
    description,
    imgPath,
  };
};

export const Team: FC = () => {
  const {
    data: {
      authData: { user },
    },
  } = useAppContext();
  const eventId = useEventId();

  const {
    teamsAllowed,
    isLoadingTeams,
    teamInfo,
    teamPhoto,
    teamMembers: teamMembersOrUndefined,
    teams,
    teamData,
  } = useTeams(eventId);
  const teamMembers = teamMembersOrUndefined as TeamMemberProps[];
  const [shuffledTeam, setShuffledTeam] = useState<TeamMemberProps[]>([]);

  const { data: evergreen, isInitialLoading: isLoadingEvergreenCMSContent } =
    useEvergreen({
      variation: eventId.toString(),
    });

  const createTeamCMS: TeamProps = teamProps(
    evergreen,
    FragmentIdentifier.createTeam
  );
  const inviteTeamCMS: TeamProps = teamProps(
    evergreen,
    FragmentIdentifier.teamInvite
  );
  const joinTeamCMS: TeamProps = teamProps(
    evergreen,
    FragmentIdentifier.joinTeam
  );

  // Only shuffle the team after teams are loaded. This prevents excessive renders
  // on child component <YourTeam>
  const isLoading = isLoadingTeams || isLoadingEvergreenCMSContent;
  useEffect(() => {
    if (teamsAllowed && !isLoading) {
      setShuffledTeam(shuffleArray(Array.from(teamMembers || [])));
    }
    // Adding teamMembers as an additional array dependency causes unnecessary renders,
    // so disable warning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingTeams]);

  const isACaptain = isATeamCaptain(teams, user);

  return (
    <Stack direction={['column', 'column', 'column', 'row']} spacing={6}>
      <Wrapper>
        {user?.isOnTeam ? (
          <>
            <YourTeam
              teamInfo={teamInfo}
              teamMembers={shuffledTeam}
              teamPhoto={teamPhoto}
            />

            {isACaptain && (
              <CaptainTools
                teamData={teamData?.data as TypeOfTeam}
                teamMembers={teams?.participant as Participant[]}
              />
            )}
            {/* TODO: This is an interim solution that is an improvement over
                  the url provided by TR. This solution should be revisited. Ref FRX-2975 */}
            <TeamInvitation
              teamInvitationUrl={user?.teamPageUrl as string}
              headline={inviteTeamCMS.headline}
              description={inviteTeamCMS.description}
              imgPath={inviteTeamCMS.imgPath}
            />
          </>
        ) : (
          <CreateTeam
            headline={createTeamCMS.headline}
            description={createTeamCMS.description}
            imgPath={createTeamCMS.imgPath}
          />
        )}
      </Wrapper>
      <Wrapper>
        {user?.isOnTeam ? (
          <TeamMembers {...{ teamMembers }} />
        ) : (
          <JoinTeam
            headline={joinTeamCMS.headline}
            description={joinTeamCMS.description}
            imgPath={joinTeamCMS.imgPath}
          />
        )}
      </Wrapper>
    </Stack>
  );
};
